/**
 * Bible Mysteries Decoder — w03_sodom, per-production overlay composition.
 * Generated by bd_overlay_gen.py — anchors resolved against
 * productions.word_timestamps_hindi (English words), in script order.
 *
 * Devotional route: NO logo here (ffmpeg adds it from channel_config).
 * All times are S(seconds) so the comp is correct at 16fps or 24fps.
 * 37 events (FactTag = citation/date/person/place/manuscript; BigMoment = reveal/verdict/myth-bust).
 */
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { FactTag, BigMoment } from "./BibleDecoderOverlayFinal";
import { loadFont as loadMono } from "@remotion/google-fonts/IBMPlexMono";

const { fontFamily: mono } = loadMono("normal", { weights: ["400", "500"], subsets: ["latin"] });
const TEXT_SHADOW = "0 2px 6px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.9), 0 0 22px rgba(0,0,0,0.55)";

const Wordmark: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const fade = interpolate(frame, [0, Math.round(2.25 * fps)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div
      style={{
        position: "absolute", bottom: 18, right: 46.67,
        fontFamily: mono, fontSize: 11, letterSpacing: 2.4, fontWeight: 500,
        color: "#ffffff", textTransform: "uppercase",
        textShadow: TEXT_SHADOW, opacity: fade,
      }}
    >
      Bible Mysteries Decoder
    </div>
  );
};

const Hold: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = Math.round(0.6 * fps);
  const out = interpolate(frame, [dur - f, dur], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: out }}>{children}</AbsoluteFill>;
};

export const BibleDecoderW03Overlays: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      <Wordmark />
      {/*  1 · FactTag · THE WORD · anchor: It became a word */}
      <Sequence from={S(3.576)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="THE WORD" value="A city's name became a lowercase noun" />
        </Hold>
      </Sequence>
      {/*  3 · BigMoment · THE HINGE · anchor: the meaning of a single Hebrew verb */}
      <Sequence from={S(69.566)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE HINGE" headline="One Hebrew verb decides everything" sub="Change how you read one word, add a prophet 400 years later, and the crime rearranges" />
        </Hold>
      </Sequence>
      {/*  4 · FactTag · LOCATION · anchor: on a plain in the Jordan valley near the Dead Sea */}
      <Sequence from={S(96.745)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="LOCATION" value="The Jordan plain, by the Dead Sea" />
        </Hold>
      </Sequence>
      {/*  5 · BigMoment · BEFORE JUDGMENT · anchor: like the garden of the Lord */}
      <Sequence from={S(120.824)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="BEFORE JUDGMENT" headline="“Like the garden of the Lord”" sub="Genesis first shows Sodom as paradise: well-watered, the best land a man could pick" />
        </Hold>
      </Sequence>
      {/*  6 · FactTag · PERSON · anchor: a man named Lot */}
      <Sequence from={S(131.053)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="PERSON" value="Lot pitches his tents toward Sodom" />
        </Hold>
      </Sequence>
      {/*  7 · FactTag · THE ECONOMY · anchor: seeps bitumen, natural asphalt */}
      <Sequence from={S(146.92)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="THE ECONOMY" value="Bitumen, natural asphalt, was treasure" />
        </Hold>
      </Sequence>
      {/*  8 · FactTag · GENESIS · anchor: the valley was full of tar pits */}
      <Sequence from={S(165.647)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="GENESIS" value="“The valley was full of tar pits”" />
        </Hold>
      </Sequence>
      {/*  9 · BigMoment · THE SETUP · anchor: This was a wealthy city on a fat plain */}
      <Sequence from={S(179.486)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE WEALTH" headline="A wealthy city on a fat plain" sub="Tar, farmland, trade roads between empires. Its wealth is the whole problem." />
        </Hold>
      </Sequence>
      {/* 10 · FactTag · THE GATE · anchor: he was sitting in the gate */}
      <Sequence from={S(199.149)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="THE GATE" value="Courthouse · market · council · square" />
        </Hold>
      </Sequence>
      {/* 11 · BigMoment · THE PEOPLE AT THE EDGES · anchor: the widow, the orphan, the beggar */}
      <Sequence from={S(258.918)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE PEOPLE AT THE EDGES" headline="The widow, orphan, beggar, stranger" sub="Remember them. When the Bible names what went wrong, it names exactly them." />
        </Hold>
      </Sequence>
      {/* 12 · BigMoment · ABRAHAM'S BARGAIN · anchor: spare the place for fifty */}
      <Sequence from={S(314.823)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="ABRAHAM'S BARGAIN" headline="Spared for just ten decent people" sub="Fifty, forty, thirty, twenty, ten. For the sake of ten, I will not destroy it." />
        </Hold>
      </Sequence>
      {/* 13 · BigMoment · THE SILENCE · anchor: the city could not produce even ten */}
      <Sequence from={S(345.887)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE SILENCE" headline="The city could not produce even ten" sub="A place doesn't fail a test that low overnight. That's the arithmetic behind the charge." />
        </Hold>
      </Sequence>
      {/* 14 · FactTag · THE VISITORS · anchor: Two visitors come to Sodom at evening */}
      <Sequence from={S(367.046)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="THE VISITORS" value="Two angels arrive at nightfall" />
        </Hold>
      </Sequence>
      {/* 15 · BigMoment · THE LAW OF HOSPITALITY · anchor: That was the law of hospitality */}
      <Sequence from={S(404.29)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE LAW OF HOSPITALITY" headline="Protect the stranger under your roof" sub="Unwritten and heavier than any code. To abuse a guest was among the deepest crimes." />
        </Hold>
      </Sequence>
      {/* 16 · BigMoment · LOT'S FEAR IS EVIDENCE · anchor: terrified of what his own city does */}
      <Sequence from={S(453.46)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="LOT'S FEAR IS EVIDENCE" headline="Terrified of what his own city does" sub="A man of standing, afraid of what Sodom does to strangers who sleep in its streets" />
        </Hold>
      </Sequence>
      {/* 17 · BigMoment · THE WHOLE CITY · anchor: both young and old, all the people from every quarter */}
      <Sequence from={S(478.959)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE WHOLE CITY" headline="Both young and old, from every quarter" sub="Not a gang, not a fringe. The entire town gathered at one door." />
        </Hold>
      </Sequence>
      {/* 18 · FactTag · THE DEMAND · anchor: that we may know them */}
      <Sequence from={S(501.157)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="THE DEMAND" value="“…that we may know them”" />
        </Hold>
      </Sequence>
      {/* 19 · BigMoment · THE HINGE VERB · anchor: In Hebrew it is yada */}
      <Sequence from={S(507.325)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE HINGE VERB" headline="Yada: “to know”" sub="Interrogate and expose, or assault? The whole traditional reading rests on deciding which" />
        </Hold>
      </Sequence>
      {/* 20 · FactTag · FREQUENCY · anchor: more than nine hundred times in the Hebrew Bible */}
      <Sequence from={S(513.119)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="FREQUENCY" value="Yada · 900+ times, rarely sexual" />
        </Hold>
      </Sequence>
      {/* 21 · BigMoment · LOT'S OFFER · anchor: He offers the mob his own two daughters */}
      <Sequence from={S(560.897)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="LOT'S OFFER" headline="He offers the mob his own daughters" sub="A horrifying line, and proof of how absolute the duty to protect the guest had become" />
        </Hold>
      </Sequence>
      {/* 22 · FactTag · PROPHET · anchor: a prophet named Ezekiel stops */}
      <Sequence from={S(622.64)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="PROPHET" value="Ezekiel names the actual charge" />
        </Hold>
      </Sequence>
      {/* 23 · FactTag · CITATION · anchor: Ezekiel, chapter sixteen */}
      <Sequence from={S(655.721)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="CITATION" value="Ezekiel 16" />
        </Hold>
      </Sequence>
      {/* 24 · BigMoment · THE BURIED VERDICT · anchor: pride, fullness of bread, and prosperous ease */}
      <Sequence from={S(673.56)} durationInFrames={S(11.000)}>
        <Hold dur={S(11.000)}>
          <BigMoment kicker="THE BURIED VERDICT" headline="Pride · fullness of bread · prosperous ease · no aid to the poor" sub="The Bible, in its own words, defining the sin of Sodom. Ezekiel 16:49." />
        </Hold>
      </Sequence>
      {/* 38 · BigMoment · THE HONEST WRINKLE · anchor: did what he calls an abomination */}
      <Sequence from={S(728.604)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE HONEST WRINKLE" headline="The darker reading isn't erased" sub="Ezekiel also calls it an “abomination,” but the word is broad and doesn't on its own name a sexual act" />
        </Hold>
      </Sequence>
      {/* 25 · BigMoment · THE CORONER'S FINDING · anchor: the first thing he finds is not lust */}
      <Sequence from={S(756.392)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE CORONER'S FINDING" headline="Not lust. A city that shared nothing" sub="It had everything and shared none of it. What the text names first is the comfort behind the door" />
        </Hold>
      </Sequence>
      {/* 26 · BigMoment · THE REORGANIZATION · anchor: It was the same sin, grown to its full */}
      <Sequence from={S(795.438)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE REORGANIZATION" headline="The same sin, grown to full size" sub="The assault at the door wasn't a break from the city's character. It was its final expression." />
        </Hold>
      </Sequence>
      {/* 27 · FactTag · CITATION · anchor: Judges, chapter nineteen */}
      <Sequence from={S(815.108)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="CITATION" value="Judges 19 · Gibeah, the same story" />
        </Hold>
      </Sequence>
      {/* 28 · FactTag · CITATION · anchor: Isaiah, chapter one, verse ten */}
      <Sequence from={S(836.14)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="CITATION" value="Isaiah 1:10 · “you rulers of Sodom”" />
        </Hold>
      </Sequence>
      {/* 29 · BigMoment · WHEN JESUS INVOKES SODOM · anchor: When Jesus himself invokes Sodom */}
      <Sequence from={S(855.854)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="WHEN JESUS INVOKES SODOM" headline="He ties it to turning away the traveler" sub="Any town that refuses to welcome them faces a judgment worse than Sodom's" />
        </Hold>
      </Sequence>
      {/* 30 · FactTag · CITATION · anchor: the book of Wisdom, chapter nineteen */}
      <Sequence from={S(876.366)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="CITATION" value="Wisdom 19 · refusing strangers" />
        </Hold>
      </Sequence>
      {/* 31 · BigMoment · WHERE THE WORD CAME FROM · anchor: does not come from Genesis or Ezekiel or Jesus */}
      <Sequence from={S(913.182)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="WHERE THE WORD CAME FROM" headline="Not biblical at all" sub="The abstract lowercase noun comes from neither Genesis, Ezekiel, nor Jesus" />
        </Hold>
      </Sequence>
      {/* 32 · FactTag · PERSON · anchor: a reforming monk named Peter Damian */}
      <Sequence from={S(924.676)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="PERSON" value="Peter Damian · c. 1050 AD" />
        </Hold>
      </Sequence>
      {/* 33 · BigMoment · THE WORD ERASED THE CITY · anchor: the word erased the city */}
      <Sequence from={S(965.21)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE WORD ERASED THE CITY" headline="A medieval label laid backward onto an ancient city" sub="The name stuck so hard it swallowed the text. The city became a word, and the word erased the city." />
        </Hold>
      </Sequence>
      {/* 34 · BigMoment · THE MIRROR · anchor: held it up as a mirror */}
      <Sequence from={S(1014.097)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE MIRROR" headline="Ezekiel aimed it at his own people" sub="He held Sodom up as a mirror to Jerusalem: this is you, if you keep getting rich and looking away" />
        </Hold>
      </Sequence>
      {/* 35 · BigMoment · THE DIAGNOSTIC · anchor: diagnostics do not expire */}
      <Sequence from={S(1073.962)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE DIAGNOSTIC" headline="Four questions, not four facts" sub="Pride. Fullness of bread. Prosperous ease. A hand not opened to the poor. Diagnostics don't expire." />
        </Hold>
      </Sequence>
      {/* 36 · BigMoment · THE VERDICT · anchor: the one-word version was hammered on */}
      <Sequence from={S(1211.525)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE VERDICT" headline="Not one sin. A full stomach, an idle hand, a shut door" sub="The one-word version was hammered on centuries later by men who never read Ezekiel out loud" />
        </Hold>
      </Sequence>
      {/* 37 · BigMoment · BIBLE MYSTERIES DECODER · anchor: one buried verse at a time, until */}
      <Sequence from={S(1263.246)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="BIBLE MYSTERIES DECODER" headline="One buried verse at a time" sub="Until the story everyone quotes and the story written on the page are the same story" />
        </Hold>
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
